/**
 * Modal "Importar de URL" — IA-assisted scrape + preview.
 *
 * OpenSpec add-categorized-catalog-guideline (PR 2 Fase 4).
 *
 * Fluxo:
 *  1. Admin cola URL + categoria default
 *  2. Click "Buscar e preencher" → POST /api/catalog/import/scrape
 *  3. ai-service fetch + LLM extract retorna preview
 *  4. Modal mostra CatalogItemForm pré-populado com dados extraídos
 *  5. Admin revisa, ajusta, salva normal (POST /api/catalog/items)
 *
 * Indicador de loading durante scrape (5-15s típico).
 */
import React, { useState } from 'react';
import { scrapeCatalogUrl, createCatalogItem } from '../../services/catalog';
import { CatalogItemForm } from './CatalogItemForm';
import type { CatalogItem, CreateCatalogItemRequest } from '../../types';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};

const modal: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  width: 'min(840px, 92vw)',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 14,
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 18px',
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
const btnSecondary: React.CSSProperties = {
  padding: '10px 18px',
  background: '#fff',
  color: '#475569',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};

export const CatalogImportUrlModal: React.FC<Props> = ({ onClose, onCreated }) => {
  const [url, setUrl] = useState('');
  const [categoryLabel, setCategoryLabel] = useState('');
  const [scraping, setScraping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CatalogItem | null>(null);
  const [saving, setSaving] = useState(false);

  const handleScrape = async () => {
    if (!url.trim()) {
      setError('Cole a URL da página');
      return;
    }
    if (!categoryLabel.trim()) {
      setError('Informe uma categoria default');
      return;
    }
    setError(null);
    setScraping(true);
    try {
      const res = await scrapeCatalogUrl(url.trim(), categoryLabel.trim());
      // Adapta preview pra shape do CatalogItem que CatalogItemForm aceita.
      // Campos não-relevantes (organizationId, productId, etc) ficam dummy.
      setPreview({
        organizationId: '',
        productId: '',
        type: res.preview.type,
        categoryId: res.preview.categoryId || '',
        categoryLabel: res.preview.categoryLabel,
        name: res.preview.name,
        status: res.preview.status || 'draft',
        descriptionShort: res.preview.descriptionShort || '',
        descriptionLong: res.preview.descriptionLong || '',
        attributes: res.preview.attributes || {},
        faq: res.preview.faq,
        integrations: res.preview.integrations,
        pineconeVectorIds: [],
        createdAt: '',
        updatedAt: '',
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setScraping(false);
    }
  };

  const handleSave = async (payload: CreateCatalogItemRequest) => {
    setSaving(true);
    try {
      await createCatalogItem(payload);
      onCreated();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Phase 2: show form with preview pre-filled.
  if (preview) {
    return (
      <div style={overlay} onClick={() => !saving && onClose()}>
        <div style={modal} onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
              ✨ Revisar dados extraídos da URL
            </h3>
            <button
              type="button"
              onClick={() => !saving && onClose()}
              style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}
              disabled={saving}
            >
              ×
            </button>
          </div>
          <div
            style={{
              padding: 10,
              background: '#eef2ff',
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            ✓ A IA preencheu o formulário baseado em <strong>{url}</strong>. Confira os campos antes de salvar.
          </div>
          <CatalogItemForm
            initial={preview}
            saving={saving}
            onCancel={onClose}
            onSubmit={handleSave}
          />
        </div>
      </div>
    );
  }

  // Phase 1: URL input.
  return (
    <div style={overlay} onClick={() => !scraping && onClose()}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🌐 Importar de URL</h3>
          <button
            type="button"
            onClick={() => !scraping && onClose()}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}
            disabled={scraping}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: 12,
            background: '#eef2ff',
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          <p style={{ margin: '0 0 6px 0' }}>
            <strong>Como funciona:</strong>
          </p>
          A IA lê a página, extrai nome, descrição, atributos e FAQs em formato estruturado.
          Você revisa e ajusta antes de salvar. <strong>Latência típica:</strong> 5-15s.
        </div>

        <div style={{ marginBottom: 12 }}>
          <label
            style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}
          >
            URL da página *
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://exemplo.com/produto/devops"
            style={inputStyle}
            autoFocus
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label
            style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}
          >
            Categoria default *
          </label>
          <input
            type="text"
            value={categoryLabel}
            onChange={(e) => setCategoryLabel(e.target.value)}
            placeholder="Ex: Tecnologia"
            style={inputStyle}
          />
        </div>

        {error && (
          <div
            style={{
              padding: 10,
              background: '#fef2f2',
              color: '#b91c1c',
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={scraping}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleScrape}
            style={{ ...btnPrimary, opacity: scraping ? 0.6 : 1 }}
            disabled={scraping || !url.trim() || !categoryLabel.trim()}
          >
            {scraping ? 'Buscando…' : 'Buscar e preencher'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CatalogImportUrlModal;
